import React, { useState, useEffect } from 'react';
import { 
  ArrowRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Import workflow components
import OnboardingWorkflow from './workflow/onboarding/OnboardingWorkflow';
import ScopeBoundariesWorkflow from './workflow/scope-boundaries/ScopeBoundariesWorkflow';
import MaterialityAnalysisWorkflow from './workflow/materiality_analysis/MaterialityAnalysisWorkflow';
import DataCollectionWorkflow from './workflow/data_collection/DataCollectionWorkflow';
import CalculationWorkflow from './workflow/calculation/CalculationWorkflow';
import ESRSDisclosuresWorkflow from './workflow/esrs_disclosures/ESRSDisclosuresWorkflow';
import DraftReportWorkflow from './workflow/draft_report/DraftReportWorkflow';
import SignOffWorkflow from './workflow/sign-off/SignOffWorkflow';
import AuditQAWorkflow from './workflow/audit_qa/AuditQAWorkflow';
import PublishWorkflow from './workflow/publish/PublishWorkflow';
import AdvancedComplianceWorkflow from './workflow/advanced_compliance/AdvancedComplianceWorkflow';

function Home() {
  const { t } = useTranslation();
  const [selectedWorkflow, setSelectedWorkflow] = useState('scopeBoundaries');

  // Ensure Home loads at the top without jumping down
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const progressStages = [
    { 
      id: 'scopeBoundaries',
      name: t('workflow.home.stages.scopeBoundaries.name'), 
      status: 'Not Started', 
      description: t('workflow.home.stages.scopeBoundaries.description'), 
      color: 'bg-gray-100 text-gray-600' 
    },
    { 
      id: 'onboarding',
      name: t('workflow.home.stages.onboarding.name'), 
      status: 'Not Started', 
      description: t('workflow.home.stages.onboarding.description'), 
      color: 'bg-gray-100 text-gray-600' 
    },
    { 
      id: 'materialityAnalysis',
      name: t('workflow.home.stages.materialityAnalysis.name'), 
      status: 'Not Started', 
      description: t('workflow.home.stages.materialityAnalysis.description'), 
      color: 'bg-gray-100 text-gray-600' 
    },
    { 
      id: 'dataCollection',
      name: t('workflow.home.stages.dataCollection.name'), 
      status: 'Not Started', 
      description: t('workflow.home.stages.dataCollection.description'), 
      color: 'bg-gray-100 text-gray-600' 
    },
    { 
      id: 'calculations',
      name: t('workflow.home.stages.calculations.name'), 
      status: 'Not Started', 
      description: t('workflow.home.stages.calculations.description'), 
      color: 'bg-gray-100 text-gray-600' 
    },
    { 
      id: 'esrsDisclosures',
      name: t('workflow.home.stages.esrsDisclosures.name'), 
      status: 'Not Started', 
      description: t('workflow.home.stages.esrsDisclosures.description'), 
      color: 'bg-gray-100 text-gray-600' 
    },
    { 
      id: 'draftReport',
      name: t('workflow.home.stages.draftReport.name'), 
      status: 'Not Started', 
      description: t('workflow.home.stages.draftReport.description'), 
      color: 'bg-gray-100 text-gray-600' 
    },
    { 
      id: 'signOff',
      name: t('workflow.home.stages.signOff.name'), 
      status: 'Not Started', 
      description: t('workflow.home.stages.signOff.description'), 
      color: 'bg-gray-100 text-gray-600' 
    },
    { 
      id: 'auditQA',
      name: t('workflow.home.stages.auditQA.name'), 
      status: 'Not Started', 
      description: t('workflow.home.stages.auditQA.description'), 
      color: 'bg-gray-100 text-gray-600' 
    },
    { 
      id: 'publish',
      name: t('workflow.home.stages.publish.name'), 
      status: 'Not Started', 
      description: t('workflow.home.stages.publish.description'), 
      color: 'bg-gray-100 text-gray-600' 
    },
    { 
      id: 'advancedCompliance',
      name: t('workflow.home.stages.advancedCompliance.name'), 
      status: 'Not Started', 
      description: t('workflow.home.stages.advancedCompliance.description'), 
      color: 'bg-gray-100 text-gray-600' 
    }
  ];

  const handleWorkflowClick = (workflowId) => {
    setSelectedWorkflow(workflowId);
  };

  const renderWorkflowComponent = () => {
    switch (selectedWorkflow) {
      case 'onboarding':
        return <OnboardingWorkflow previewOnly={true} />;
      case 'scopeBoundaries':
        return <ScopeBoundariesWorkflow previewOnly={true} />;
      case 'materialityAnalysis':
        return <MaterialityAnalysisWorkflow previewOnly={true} />;
      case 'dataCollection':
        return <DataCollectionWorkflow previewOnly={true} />;
      case 'calculations':
        return <CalculationWorkflow previewOnly={true} />;
      case 'esrsDisclosures':
        return <ESRSDisclosuresWorkflow previewOnly={true} />;
      case 'draftReport':
        return <DraftReportWorkflow previewOnly={true} />;
      case 'signOff':
        return <SignOffWorkflow previewOnly={true} />;
      case 'auditQA':
        return <AuditQAWorkflow previewOnly={true} />;
      case 'publish':
        return <PublishWorkflow previewOnly={true} />;
      case 'advancedCompliance':
        return <AdvancedComplianceWorkflow previewOnly={true} />;
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Coming Soon Banner */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-lg mb-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-full">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-lg">{t('dashboard.comingSoon.title')}</h3>
              <p className="text-blue-100">{t('dashboard.comingSoon.description')}</p>
            </div>
          </div>
          <div className="hidden sm:block">
            <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-medium">
              {t('dashboard.comingSoon.status')}
            </span>
          </div>
        </div>
      </div>
                                       {/* Header */}
        <div className="mb-4">
         <div className="flex justify-between items-start">
           <h1 className="text-3xl font-bold text-gray-900">{t('workflow.home.title')}</h1>
                      {/* <div className="text-center -mt-2 -mr-2">
              <div className="relative w-16 h-16 mx-auto">
                <svg className="w-16 h-16" viewBox="0 0 36 36">
                  <path
                    className="text-gray-200"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                                    <path
                     className="text-blue-600"
                     stroke="currentColor"
                     strokeWidth="3"
                     strokeDasharray="0, 100"
                     strokeLinecap="round"
                     fill="none"
                     d="M18 2.0845
                       a 15.9155 15.9155 0 0 1 0 31.831
                       a 15.9155 15.9155 0 0 1 0 -31.831"
                   />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-700">0%</span>
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1">Complete</div>
            </div> */}
         </div>
       </div>

      {/* ESG Report Progress */}
      <div className="mb-8">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-900">{t('workflow.home.esgReportProgress')}</h2>
        </div>

        {/* Progress Cards */}
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <div className="flex gap-4 pb-2" style={{ minWidth: 'max-content' }}>
            {progressStages.map((stage, index) => (
              <div 
                key={index} 
                className={`bg-white p-4 rounded-lg shadow-sm border border-gray-200 min-w-[280px] flex-shrink-0 ${
                  ['onboarding', 'scopeBoundaries', 'dataCollection', 'calculations', 'draftReport', 'materialityAnalysis', 'esrsDisclosures', 'signOff', 'auditQA', 'publish', 'advancedCompliance'].includes(stage.id) ? 'hover:shadow-md transition-shadow cursor-pointer' : ''
                }`}
                onClick={() => handleWorkflowClick(stage.id)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900">{stage.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${stage.color}`}>
                    {stage.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{stage.description}</p>
                {['onboarding', 'scopeBoundaries', 'dataCollection', 'calculations', 'draftReport', 'materialityAnalysis', 'esrsDisclosures', 'signOff', 'auditQA', 'publish', 'advancedCompliance'].includes(stage.id) && (
                  <div className="flex items-center text-blue-primary text-xs font-medium mt-2">
                    {t('workflow.home.clickToManage')}
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Selected Workflow */}
      {selectedWorkflow && (
        <div>
          {renderWorkflowComponent()}
        </div>
      )}
    </div>
  );
}

export default Home; 